import {
  LoginCodeChannelEnum,
  useCreateLoginCode,
  useCreateMagicLinkLogin,
  useLoginByPhoneCode,
  prettyTelString,
} from '@alice-financial/alice-sdk'
import React from 'react'
import { useForm } from 'react-hook-form'

/**
 * Types for the login form state management
 */
type LoginState = {
  /** Current mode of the login form */
  mode: 'input' | 'phone-verification' | 'email-sent'
  /** Phone verification data - only present when mode is 'phone-verification' */
  phoneVerification: {
    channel: LoginCodeChannelEnum | null
    aliceTel: string | null
  } | null
  /** Email link data - only present when mode is 'email-sent' */
  emailLink: {
    expiresInMinutes: number
  } | null
}

/**
 * Actions that can be dispatched to update the login state
 */
type LoginAction =
  | {
      type: 'PHONE_CODE_REQUESTED'
      payload: { channel: LoginCodeChannelEnum | null; aliceTel: string | null }
    }
  | { type: 'EMAIL_LINK_REQUESTED'; payload: { expiresInMinutes: number } }
  | { type: 'RESET' }

/**
 * Form values for the login form
 */
type LoginFormValues = {
  requestCodeTarget: string // phone number or email address
  verificationCode: string
}

const cleanEmail = (input: string) => {
  return input.toLowerCase().trim()
}

/**
 * Utility functions for input validation
 */
const isEmailAddress = (input: string): boolean => {
  // very lax email validation - require @ and . characters in string
  return /^\S+@\S+\.\S+$/.test(input)
}

// remove leading 1 and all non-digit characters
const cleanTel = (input: string) => {
  return input.replace(/^1|(\D)/g, '')
}

const isPhoneNumber = (input: string): boolean => {
  // Remove all non-digit characters and check if it's a 10-digit number
  const cleaned = cleanTel(input)
  return /^\d{10}$/.test(cleaned)
}

/**
 * Reducer function for managing login form state
 * Ensures mutually exclusive states and atomic updates
 */
const loginReducer = (state: LoginState, action: LoginAction): LoginState => {
  switch (action.type) {
    case 'PHONE_CODE_REQUESTED':
      return {
        mode: 'phone-verification',
        phoneVerification: {
          channel: action.payload.channel,
          aliceTel: action.payload.aliceTel,
        },
        emailLink: null,
      }

    case 'EMAIL_LINK_REQUESTED':
      return {
        mode: 'email-sent',
        phoneVerification: null,
        emailLink: {
          expiresInMinutes: action.payload.expiresInMinutes,
        },
      }

    case 'RESET':
      return {
        mode: 'input',
        phoneVerification: null,
        emailLink: null,
      }

    default:
      return state
  }
}

/**
 * Initial state for the login form
 */
const initialState: LoginState = {
  mode: 'input',
  phoneVerification: null,
  emailLink: null,
}

/**
 * Return type for the useTelLoginForm hook
 */
export type UsePasswordlessLoginFormReturn = {
  // Form control
  control: ReturnType<typeof useForm<LoginFormValues>>['control']

  // Current state
  mode: 'input' | 'phone-verification' | 'email-sent'

  // Loading state
  isFetching: boolean

  // Mode-specific data (only populated when relevant)
  phoneVerification: {
    channel: LoginCodeChannelEnum | null
    aliceTel: string | null
    prettyTel: string | null // pre-formatted
  } | null
  emailLink: {
    expiresInMinutes: number
  } | null

  // Event handlers
  onRequestCode: () => void
  onSubmitPhoneCode: () => void
  resetForm: () => void

  // Validation utilities (for form validation)
  isEmailAddress: typeof isEmailAddress
  isPhoneNumber: typeof isPhoneNumber
}

/**
 * Custom hook that manages the telephone/email login form state and behavior
 *
 * This hook encapsulates all the complex state management, API calls, and form handling
 * for the login form that supports both phone number and email address authentication.
 *
 * Features:
 * - Supports both phone number (SMS verification) and email (magic link) login flows
 * - Uses useReducer for predictable state management with mutually exclusive states
 * - Handles form validation and submission
 * - Manages loading states and error handling
 * - Provides clean separation between UI logic and business logic
 *
 * @returns Object containing all state and handlers needed by the UI component
 */
export const usePasswordlessLoginForm = (): UsePasswordlessLoginFormReturn => {
  // State management using useReducer for complex, related state
  const [state, dispatch] = React.useReducer(loginReducer, initialState)

  // Form management with react-hook-form
  const { control, handleSubmit, setValue, getValues } = useForm<LoginFormValues>({
    defaultValues: { requestCodeTarget: '', verificationCode: '' },
  })

  // Compute grouped data objects from reducer state
  const phoneVerification = state.phoneVerification
    ? {
        channel: state.phoneVerification.channel,
        aliceTel: state.phoneVerification.aliceTel ? prettyTelString(state.phoneVerification.aliceTel) : null,
        prettyTel: prettyTelString(getValues('requestCodeTarget')),
      }
    : null

  const emailLink = state.emailLink

  // Phone verification code mutation
  const { mutate: createLoginCode, isLoading: createLoginCodeIsLoading } = useCreateLoginCode({
    onSuccess: (data) => {
      dispatch({
        type: 'PHONE_CODE_REQUESTED',
        payload: {
          channel: data.createLoginCode?.channel || null,
          aliceTel: data.createLoginCode?.aliceTel || null,
        },
      })
    },
  })

  // Email magic link mutation
  const { mutate: createEmailLoginLink, isLoading: createEmailLoginLinkIsLoading } = useCreateMagicLinkLogin({
    onSuccess: (data) => {
      const expiresInMinutes = data.createMagicLinkLogin?.expiresInMinutes
      if (expiresInMinutes) {
        dispatch({
          type: 'EMAIL_LINK_REQUESTED',
          payload: {
            expiresInMinutes,
          },
        })
      }
    },
  })

  // Phone code verification mutation
  const { mutate: loginByPhone, isLoading: loginByPhoneIsLoading } = useLoginByPhoneCode()

  // Computed loading state
  const isRequestingCode = createLoginCodeIsLoading || createEmailLoginLinkIsLoading

  /**
   * Resets the form to initial state
   * Clears both the reducer state and form values
   */
  const resetForm = React.useCallback(() => {
    dispatch({ type: 'RESET' })
    setValue('verificationCode', '')
  }, [setValue])

  /**
   * Handles the initial form submission to request a code or email link
   * Determines whether the input is an email or phone number and calls appropriate API
   */
  const onRequestCode = handleSubmit(
    React.useCallback(
      (values: LoginFormValues) => {
        const inputValue = values.requestCodeTarget.trim()

        if (isEmailAddress(inputValue)) {
          createEmailLoginLink({ email: cleanEmail(inputValue) })
        } else if (isPhoneNumber(inputValue)) {
          createLoginCode({ tel: cleanTel(inputValue) })
        }
      },
      [createEmailLoginLink, createLoginCode]
    )
  )

  /**
   * Handles phone verification code submission
   * Only called when in phone verification mode
   * Includes loading state handling internally
   */
  const onSubmitPhoneCode = handleSubmit(
    React.useCallback(
      (values: LoginFormValues) => {
        if (!loginByPhoneIsLoading) {
          loginByPhone({ tel: values.requestCodeTarget, code: values.verificationCode })
        }
      },
      [loginByPhone, loginByPhoneIsLoading]
    )
  )

  return {
    // Form control
    control,

    // Current state
    mode: state.mode,

    // Loading state
    isFetching: isRequestingCode,

    // Mode-specific data
    phoneVerification,
    emailLink,

    // Event handlers
    onRequestCode,
    onSubmitPhoneCode,
    resetForm,

    // Validation utilities
    isEmailAddress,
    isPhoneNumber,
  }
}
